// `shortlist.delete-alert-dialogue.tpl.html` and `shortlist.change-state-dialogue.tpl.html`
// — the two confirmations the shortlist asks for, with their window classes.
//
// ⚠️ THE WINDOW CLASS CHAIN IS WHAT THE STYLESHEETS SELECT, not decoration. Upstream opens
// these through `modalService.show(..., { windowClass: 'dialog-unified shortlist-dialogue
// delete-alert-dialogue-container-slim' })`, and `_shortlist.delete-alert-dialogue.scss`
// hangs the 180px slim width, the red button and the left-aligned copy off
// `.delete-alert-dialogue-container-slim .modal-dialog` / `.modal-body`. Rendering a plain
// div loses all three, so both render inside `UnifiedModal`, which reproduces what
// `modalService.show({ useUnifiedTemplate: true })` draws: `.modal-dialog > .modal-content >
// .modal-content-inner > .modal-header + .modal-body`.
//
// ⚠️ THEY USED TO BUILD THAT CHAIN BY HAND, AND LEFT TWO LAYERS OUT. `.modal-content-inner` and the
// empty `.modal-header` (collapsed to zero height, its close button hidden by the stylesheet) are
// the unified template's own; without them the Exchange dialogue — built the same way — measured
// one grey level off in its shadow all round against the dev tenant. Going through one shell is
// also what gives these upstream's Escape, backdrop click and focus move.
//
// ⚠️ AND THE SLIM VARIANT IS A DIFFERENT CLASS, NOT A WIDTH. `isSlimMode` picks
// `delete-alert-dialogue-container-slim`; the panel is 200px wide, and the full-view class
// forces `max-width: 401px`, which overflows it.
import type { ReactElement, ReactNode } from 'react'
import { UnifiedModal } from '../modal/UnifiedModal.tsx'

export interface ShortlistDialogueProps {
  /** The window class upstream passes — the third token of `windowClass`. */
  containerClass: string
  children: ReactNode
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
}

export function ShortlistDialogue({
  containerClass,
  children,
  confirmLabel,
  onConfirm,
  onCancel,
}: ShortlistDialogueProps): ReactElement {
  return (
    <UnifiedModal open windowClass={`dialog-unified shortlist-dialogue ${containerClass}`} onClose={onCancel}>
      <div>
        <div className="shortlist-dialogue-x-icon">
          <i
            className="far fa-times"
            role="button"
            aria-label="Close"
            tabIndex={0}
            onClick={onCancel}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onCancel()
            }}
          />
        </div>
        {children}
        <div className="shortlist-dialogue-actions">
          <div>
            <button type="button" onClick={onConfirm}>
              {confirmLabel}
            </button>
          </div>
          <div>
            <a
              href=""
              onClick={(event) => {
                event.preventDefault()
                onCancel()
              }}
            >
              Cancel
            </a>
          </div>
        </div>
      </div>
    </UnifiedModal>
  )
}

export interface DeleteAlertDialogueProps {
  alertName: string
  /** The injected panel is always slim; the full view is the standalone page. */
  slim?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function DeleteAlertDialogue({
  alertName,
  slim = true,
  onConfirm,
  onCancel,
}: DeleteAlertDialogueProps): ReactElement {
  return (
    <ShortlistDialogue
      containerClass={slim ? 'delete-alert-dialogue-container-slim' : 'delete-alert-dialogue-container'}
      confirmLabel="Delete"
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <div className="delete-alert-dialogue-cta">
        {/* "Hunter" is upstream's word for a saved search, and it is in the copy. */}
        <span>
          Are you sure you want to delete <span className="alert-name">{alertName}</span> Hunter?
        </span>
      </div>
    </ShortlistDialogue>
  )
}

/**
 * `shortlist.alert-dialogue-generic.tpl.html` — the body `showGenericDialogue` renders.
 *
 * ⚠️ ITS INNER CHAIN IS NOT `ShortlistDialogue`'s, and the stylesheets select on the
 * difference: this one is `.shortlist-alert-dialogue-generic-container` wrapping
 * `-cta` and `-actions`, where the delete dialogue uses `.shortlist-dialogue-actions`.
 * `_shortlist.alert-dialogue-generic.scss` hangs the orange confirm button and the content
 * padding off these names, so reusing the other component's markup loses both.
 *
 * `windowClass` upstream is `dialog-unified shortlist-dialogue
 * shortlist-alert-dialogue-generic-container <caller's>`, which is what `containerClass`
 * carries here.
 */
export function GenericDialogue({
  containerClass,
  children,
  confirmLabel = 'Yes',
  onConfirm,
  onCancel,
}: {
  containerClass?: string
  children: ReactNode
  /** `{{ ::(!!confirmBtnText ? confirmBtnText : 'Yes') }}`. */
  confirmLabel?: string
  onConfirm: () => void
  onCancel: () => void
}): ReactElement {
  return (
    <UnifiedModal
      open
      windowClass={`dialog-unified shortlist-dialogue shortlist-alert-dialogue-generic-container${
        containerClass === undefined ? '' : ` ${containerClass}`
      }`}
      onClose={onCancel}
    >
      <div className="shortlist-alert-dialogue-generic-container">
        <div className="shortlist-dialogue-x-icon">
          <i
            className="far fa-times"
            role="button"
            aria-label="Close"
            tabIndex={0}
            onClick={onCancel}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onCancel()
            }}
          />
        </div>
        <div className="shortlist-alert-dialogue-generic-cta">{children}</div>
        <div className="shortlist-alert-dialogue-generic-actions">
          <div>
            <button type="button" onClick={onConfirm}>
              {confirmLabel}
            </button>
          </div>
          <div>
            <a
              href=""
              onClick={(event) => {
                event.preventDefault()
                onCancel()
              }}
            >
              Cancel
            </a>
          </div>
        </div>
      </div>
    </UnifiedModal>
  )
}

export interface ActivateAutomationDialogueProps {
  alertName: string
  atsLabel: string
  /** `extensionTabData.totalResults` — how many people this will message. */
  totalResults: number
  onConfirm: () => void
  onCancel: () => void
}

/**
 * The confirmation in front of `enableAutomation`, copy for copy.
 *
 * ⚠️ IT IS THE OTHER HALF OF THE BUTTON, NOT A COURTESY. What the button does — mail every
 * matching candidate and keep mailing new ones — is stated here and nowhere else in the UI,
 * so a port that wires the click without this turns a decision into a click.
 */
export function ActivateAutomationDialogue({
  alertName,
  atsLabel,
  totalResults,
  onConfirm,
  onCancel,
}: ActivateAutomationDialogueProps): ReactElement {
  return (
    <GenericDialogue
      containerClass="activate-automation-generic-dialog-container"
      confirmLabel="Activate Automation"
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <div className="activate-automation-generic-dialog-content">
        <b>
          Activate {atsLabel} Automation for {alertName} hunter
        </b>
        <br />
        <br />
        <p className="description">
          By activating, all matching candidates for {alertName} hunter ({totalResults}) will be
          messaged using the &quot;Woo Hunter&quot; {atsLabel} automation.
          <br />
          <br />
          This automation dynamically updates with every new candidate information found by Woo.
        </p>
      </div>
    </GenericDialogue>
  )
}
